import React, { useEffect, useMemo, useState } from 'react';
import { avecPerimetre } from './perimetre';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tableauBord as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { sectionsGroupes, basculerGroupe } from './regroupements';
import { usePanneau, COULEURS } from './ventesUtils';
import './Stock.css';
import './TableauBord.css';

// ─── Tableau de bord général (haut de l'accueil) ─────────────────────────────
// Une ligne par métier, trois jauges par ligne : c'est la lecture horizontale
// demandée — VN Volume / CA / Marge, puis VO, puis APV, puis PR.
//
// ⚠ Chaque jauge NOMME son repère. Le budget 2026 est incomplet (Blue
// Automobiles absente de tout, heures APV saisies sur 2 concessions sur 5) :
// là où il manque, la jauge se compare au MOIS PRÉCÉDENT et le dit, plutôt que
// d'afficher un « +27 % » qui ne mesurerait que le trou du fichier.

const nfr = new Intl.NumberFormat('fr-FR');
const fmt = (v, u) => {
  if (v == null) return '—';
  if (u === 'pct') return `${nfr.format(Math.round(v * 10) / 10)} %`;
  if (u === 'nb') return nfr.format(Math.round(v));
  const a = Math.abs(v);
  if (a >= 1e6) return `${nfr.format(Math.round(v / 1e5) / 10)} M€`;
  if (a >= 1e3) return `${nfr.format(Math.round(v / 1e2) / 10)} k€`;
  return `${nfr.format(Math.round(v))} €`;
};
const moisLong = (ym) => {
  const [a, m] = (ym || '2026-01').split('-');
  return new Date(Number(a), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

// ─── L'arc ───────────────────────────────────────────────────────────────────
// Demi-cercle, dessiné à la main : aucune bibliothèque pour trois segments de
// cercle. La part remplie est le réalisé rapporté au repère à date, bornée à
// 100 % — au-delà, c'est l'écart chiffré qui porte l'information, pas un arc
// qui déborderait.
function Arc({ part, ton }) {
  const R = 52, C = Math.PI * R;
  const p = Math.max(0, Math.min(1, part || 0));
  return (
    <svg className="tb-arc" viewBox="0 0 132 74" role="img" aria-hidden="true">
      <path d="M 14 66 A 52 52 0 0 1 118 66" fill="none" strokeWidth="17"
        className="tb-arc-fond" strokeLinecap="butt" />
      <path d="M 14 66 A 52 52 0 0 1 118 66" fill="none" strokeWidth="17"
        className={`tb-arc-plein ${ton}`} strokeLinecap="butt"
        strokeDasharray={`${C * p} ${C}`} />
    </svg>
  );
}

function Jauge({ m, onClic }) {
  const ref = m.reference;
  const ecart = ref ? m.realise - ref : null;
  const pct = ref ? ((m.realise - ref) / Math.abs(ref)) * 100 : null;
  // Le ton dit l'écart au repère, jamais l'identité (CLAUDE.md §5.3).
  const ton = pct == null ? 'neutre' : pct >= -2 ? 'favorable' : pct >= -10 ? 'attention' : 'defavorable';
  return (
    <div className="tb-jauge" onClick={onClic} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClic(); }}>
      <div className="tb-jauge-t">{m.nom}</div>
      <div className="tb-jauge-c">
        <div className="tb-arc-wrap">
          <Arc part={ref ? m.realise / ref : 0} ton={ton} />
          <div className="tb-val">{fmt(m.realise, m.unite)}</div>
        </div>
        <div className="tb-ref">
          {m.nonPubliee ? <div className="tb-ref-l">Donnée non publiée<br /><i>par la source</i></div> : <>
          <div className="tb-ref-l">{m.source === 'objectif' ? 'Objectif' : m.source === 'budget' ? 'Budget' : m.source === 'n-1' ? 'Même période N-1' : 'Mois précédent'}<br /><i>{m.ratio ? '(de la période)' : '(à date)'}</i></div>
          <div className="tb-ref-v">{fmt(ref, m.unite)}</div>
          {ecart != null && (
            <div className={`tb-ref-e ${ton}`}>
              {/* L'écart d'un TAUX se dit en points : « +0,1 % » à côté de
                  « +0,3 % » ferait lire deux fois la même chose, et faux. */}
              {ecart > 0 ? '+' : ''}
              {m.unite === 'pct' ? `${nfr.format(Math.round(ecart * 10) / 10)} pt` : fmt(ecart, m.unite)}<br />
              {pct > 0 ? '+' : ''}{nfr.format(Math.round(pct * 10) / 10)} %
            </div>
          )}
          </>}
        </div>
      </div>
    </div>
  );
}

// ─── Trésorerie et créances (2026-09-30) ────────────────────────────────────
// De l'INFORMATION, pas une performance : il n'y a pas d'objectif, donc pas
// de jauge ni de couleur de jugement. Un montant, sa date, et ce qui le
// compose. Mêmes chiffres que les rapports Trésorerie et Créances clients.
const dateCourte = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : null);
function Info({ titre, valeur, lignes, onClic, children }) {
  return (
    <div className="tb-info" onClick={onClic} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClic(); }}>
      <div className="tb-jauge-t">{titre}</div>
      <div className="tb-info-v">{valeur}</div>
      {lignes.filter(Boolean).map((l, i) => <div key={i} className="tb-info-l">{l}</div>)}
      {children}
    </div>
  );
}

// ─── Stock (2026-10-31) ──────────────────────────────────────────────────────
// Même cadre que Trésorerie et créances : un montant, sa date, et une simple
// courbe de tendance (fins de mois puis photo du jour), sans axe ni couleur
// de jugement — elle situe, elle ne note pas.
function Tendance({ serie }) {
  const pts = (serie || []).filter((p) => p.v != null);
  if (pts.length < 2) return null;
  const W = 220; const H = 38; const min = Math.min(...pts.map((p) => p.v)); const max = Math.max(...pts.map((p) => p.v));
  const xy = pts.map((p, i) => [Math.round((i / (pts.length - 1)) * W * 10) / 10, Math.round((H - 4 - ((p.v - min) / ((max - min) || 1)) * (H - 8)) * 10) / 10]);
  const [lx, ly] = xy[xy.length - 1];
  const titre = pts.map((p) => `${libPoint(p.ym)} : ${fmt(p.v, 'eur')}`).join('\n');
  return (
    <svg className="tb-tendance" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Tendance">
      <title>{titre}</title>
      <polyline points={xy.map((c) => c.join(',')).join(' ')} fill="none" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="2.5" />
    </svg>
  );
}
// Un point est un mois (fin de mois), un jour (AAAA-MM-JJ) ou « jour » (la photo).
const libPoint = (ym) => (ym === 'jour' ? 'aujourd’hui' : /^\d{4}-\d{2}-\d{2}/.test(ym) ? dateCourte(ym) : moisLong(ym));
const depuis = (serie) => {
  const p = (serie || []).filter((x) => x.ym !== 'jour');
  if (p.length < 2) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(p[0].ym) ? `depuis le ${dateCourte(p[0].ym)}` : `depuis fin ${moisLong(p[0].ym)}`;
};

const PERIODES = [{ c: 'mois', l: 'Ce mois' }, { c: 'ytd', l: 'Cumul année' }];

// Accueil d'Auto Antilles et d'Auto Métropole (2026-09-30) : le tableau de
// bord SEUL, mis en page comme un rapport — contenu centré, panneau de filtres
// à droite (plaques, concessions groupées, sociétés), choix « Ce mois / Cumul
// année » dans l'en-tête du bloc.
export default function TableauBord({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [ouvert, basculer] = usePanneau();
  const periode = params.get('periode') === 'ytd' ? 'ytd' : 'mois';
  const societesUrl = params.get('societes') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.charger({ periode, societes: societesUrl, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Tableau de bord indisponible'); });
    return () => { actif = false; };
  }, [periode, societesUrl, branche]);

  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societesDispo.map((x) => x.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societesDispo]);
  const couleurDe = useMemo(() => Object.fromEntries(societesDispo.map((x, i) => [x.code, COULEURS[i % COULEURS.length]])), [societesDispo]);
  const setFiltre = (cles) => {
    const next = new URLSearchParams(params);
    Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const toggleSociete = (code) => {
    const set = new Set(selected); set.has(code) ? set.delete(code) : set.add(code);
    const l = [...set];
    setFiltre({ societes: l.length === 0 ? '__aucune__' : l.length === societesDispo.length ? '' : l.join(',') });
  };

  const Panneau = (
    <FiltresPanel ouvert={ouvert} onToggle={basculer}
      societesDispo={societesDispo} societesActives={selected} onToggleSociete={toggleSociete}
      onToutesSocietes={() => setFiltre({ societes: '' })} onAucuneSociete={() => setFiltre({ societes: '__aucune__' })}
      groupes={sectionsGroupes(societesDispo)} onToggleGroupe={(g) => setFiltre({ societes: basculerGroupe(g, selected, societesDispo) })}
      couleurDe={couleurDe} nbActifs={societesUrl ? 1 : 0} onReset={() => setFiltre({ societes: '' })} />
  );

  if (erreur) return <><main className="vt-page tb-page"><div className="bi-empty"><h2>Tableau de bord indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page tb-page"><div className="tb-skel plein" /></main>{Panneau}</>;

  // Les concessions dont le repère manque, tous métiers confondus : l'info a
  // sa place ici, une fois, plutôt que répétée sur douze jauges.
  const sansRepere = [...new Set((data.metiers || []).flatMap((m) => m.mesures.flatMap((x) => x.manquantes || [])))];
  const ytd = data.periode === 'ytd';
  const nomPerimetre = selected.length === societesDispo.length ? 'Toutes les concessions'
    : selected.length === 1 ? societesDispo.find((x) => x.code === selected[0])?.nom : `${selected.length} concessions`;

  return (
    <>
      <main className="vt-page tb-page">
        <section className="tb tb-plein">
          <div className="tb-tete">
            <div>
              <h2>{ytd ? "Où en est l'année" : 'Où en est le mois'}</h2>
              <div className="tb-tete-d">
                <b>{ytd ? `janvier → ${moisLong(data.mois)}` : moisLong(data.mois)}</b> · au {data.jour >= 32 ? 'dernier jour' : `${data.jour}`} du mois · {nomPerimetre}
              </div>
            </div>
            <span className="st-seg">{PERIODES.map((p) => (
              <button key={p.c} type="button" className={periode === p.c ? 'on' : ''} onClick={() => setFiltre({ periode: p.c === 'mois' ? '' : p.c })}>{p.l}</button>
            ))}</span>
          </div>

          {data.vide ? (
            <div className="bi-empty"><h2>Aucune concession sélectionnée</h2><p>Cochez au moins une concession dans les filtres à droite.</p></div>
          ) : (
            <>
              {sansRepere.length > 0 && (
                <div className="tb-avert">
                  Repère = <b>{ytd ? 'la même période N-1' : 'le mois précédent'}</b> à date là où l'objectif ou le budget
                  manque — {sansRepere.length >= selected.length && selected.length > 3
                    ? <>pour <b>toutes les concessions sélectionnées</b></>
                    : <>pour {sansRepere.map((n) => n.split(' · ')[0]).join(', ')}</>}. Les objectifs se saisissent dans <b>Administration ▸ Objectifs</b>.
                </div>
              )}
              <div className="tb-lignes">
                {(data.metiers || []).map((m) => (
                  <div className="tb-ligne" key={m.code}>
                    <button type="button" className="tb-metier" onClick={() => navigate(avecPerimetre(`/r/${m.slug}`, window.location.search))}>
                      {m.nom}<span className="tb-fleche">▸</span>
                    </button>
                    <div className="tb-jauges">
                      {m.mesures.map((x) => (
                        <Jauge key={x.code} m={x} onClic={() => navigate(avecPerimetre(`/r/${m.slug}`, window.location.search))} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {data.finance && (data.finance.tresorerie || data.finance.creances) && (() => {
                const t = data.finance.tresorerie; const c = data.finance.creances;
                const arretes = [...new Set([t?.arrete, c?.arrete].filter(Boolean).map(dateCourte))];
                const vers = (k) => () => navigate(avecPerimetre(`/r/${data.finance.slugs[k]}`, window.location.search));
                const pctEchu = c && c.encours ? Math.round((c.echu / c.encours) * 100) : null;
                return (
                  <div className="tb-ligne tb-finance">
                    <div className="tb-metier tb-metier-info">
                      Trésorerie & créances clients
                      <span className="tb-info-d">{arretes.length ? `photo du ${arretes.join(' / ')}` : ''} · information, sans objectif</span>
                    </div>
                    <div className="tb-infos">
                      {t && <Info titre="Position de trésorerie" valeur={fmt(t.position, 'eur')} onClic={vers('tresorerie')}
                        lignes={['banques, encaissements, caisse et cash pooling', [t.arrete && `au ${dateCourte(t.arrete)}`, depuis(t.serie) && `courbe ${depuis(t.serie)}`].filter(Boolean).join(' · ')]}><Tendance serie={t.serie} /></Info>}
                      {t && <Info titre="Position cash pooling" valeur={fmt(t.pool, 'eur')} onClic={vers('tresorerie')}
                        lignes={['compte courant de pooling', t.pool < 0 ? 'négatif : emprunte au pool' : 'positif : prête au pool']} />}
                      {c && <Info titre="Créances clients" valeur={fmt(c.encours, 'eur')} onClic={vers('creances')}
                        lignes={[`dont échu ${fmt(c.echu, 'eur')}${pctEchu != null ? ` (${pctEchu} %)` : ''}`, `dont échu depuis plus de 90 j ${fmt(c.plus90, 'eur')}`, depuis(c.serie) && `courbe ${depuis(c.serie)}`]}><Tendance serie={c.serie} /></Info>}
                    </div>
                  </div>
                );
              })()}
              {data.stock && (data.stock.vn || data.stock.vo || data.stock.pr) && (() => {
                const st = data.stock;
                const vers = (k) => () => navigate(avecPerimetre(`/r/${st.slugs[k]}`, window.location.search));
                const arretes = [...new Set([st.vn?.arrete, st.vo?.arrete, st.pr?.arrete].filter(Boolean).map(dateCourte))];
                const tend = (x) => (x.serie || []).length > 1 ? depuis(x.serie) : 'pas d’historique de fin de mois exporté';
                return (
                  <div className="tb-ligne tb-finance">
                    <div className="tb-metier tb-metier-info">
                      Stock
                      <span className="tb-info-d">{arretes.length ? `photo du ${arretes.join(' / ')}` : ''} · information, sans objectif</span>
                    </div>
                    <div className="tb-infos">
                      {st.vn && <Info titre="Véhicules neufs" valeur={fmt(st.vn.valeur, 'eur')} onClic={vers('vn')}
                        lignes={[`${fmt(st.vn.volume, 'nb')} véhicules · valeur d’achat`, tend(st.vn)]}><Tendance serie={st.vn.serie} /></Info>}
                      {st.vo && <Info titre="Véhicules d’occasion" valeur={fmt(st.vo.valeur, 'eur')} onClic={vers('vo')}
                        lignes={[`${fmt(st.vo.volume, 'nb')} véhicules · valeur d’achat`, tend(st.vo)]}><Tendance serie={st.vo.serie} /></Info>}
                      {st.pr && <Info titre="Pièces de rechange" valeur={fmt(st.pr.valeur, 'eur')} onClic={vers('pr')}
                        lignes={['au prix d’achat', tend(st.pr)]}><Tendance serie={st.pr.serie} /></Info>}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </section>
      </main>
      {Panneau}
    </>
  );
}
